import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { getEntity, getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { isOpenGovAdmin, userSelector } from '@opengov/app-core/session/selectors';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { routerActions } from '@opengov/compose-app-router/state';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { IEntity } from '@opengov/og-api-ts';
import { generatePageTitle } from '@opengov/stories-shared/utils';
import { isEmpty } from 'lodash';

import { storiesMsgs } from '../../locale';
import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, publicationListPath } from '../../routes/paths';
import {
  ISetupOnlinePublicationProps,
  SetupOnlinePublication
} from '../components/Publications/OnlinePublication/SetupOnlinePublication';
import { ITab } from '../components/CommonComponents/TabBar/TabBar';
import { TABS_PUBLICATION } from '../components/CommonComponents/TabBar/TabBarUtils';
import { selectOnlinePublicationTranslatorFlag } from '../common/flags';
import { selectFeatureFlags } from '@opengov/feature-flag/selectors';
import { FLAG_PRODUCT_STORIES_ACCESS_CONTROL } from '@opengov/stories-shared/constants';
import {
  navigateToOnlinePublicationMenu,
  navigateToPreviewOnlinePublication,
  navigateToPublicationView,
  navigateToSetupOnlinePublicationTab
} from '../state/navigation';

// Shared selector function used by both the root and `/menu/:menuId` route.
// `state: any` is required by `IRoute['selector']` from
// `@opengov/compose-app-router` — narrowing breaks route-level assignability
// against the library's `(state: any) => any` contract.
const buildSelector = (state: any): ISetupOnlinePublicationProps => {
  const location = getLocation(state);
  const params = (location.params || {}) as { publicationId?: string; menuId?: string; tabId?: string };
  // Use Redux router params instead of window.location.pathname so that during
  // the async onEnter hold (history.push has fired but locationChanged has not
  // yet passed through the middleware), the selector still reflects the
  // pre-navigation route and doesn't flash to Navigation Menu tab.
  const tabId = params.tabId ?? '';
  const initialActiveTab: ITab | null =
    isEmpty(tabId)
      ? TABS_PUBLICATION[0]
      : TABS_PUBLICATION.find((tab) => tab.path === tabId) ?? TABS_PUBLICATION[0];

  const initialMenuId = params.menuId ?? null;
  const entity = getEntity(state);
  const msgs = storiesMsgs(state);
  return {
    publicationId: params.publicationId ?? '',
    entityId: getNumericalEntityId(state),
    entityUuid: entity.uuid,
    entityName: entity.display_name,
    entitySubdomain: getEntitySubdomain(state),
    isOpenGovAdmin: isOpenGovAdmin(state),
    enableGoogleTranslateForPublications: selectOnlinePublicationTranslatorFlag(state),
    storiesAccessControlEnabled: selectFeatureFlags(state).get(FLAG_PRODUCT_STORIES_ACCESS_CONTROL),
    messages: { ...msgs.onlinePublication },
    user: userSelector(state),
    initialActiveTab,
    initialMenuId,
    navigateToPreviewOnlinePublication,
    navigateToPublicationView,
    navigateToOnlinePublicationMenu,
    navigateToSetupOnlinePublicationTab,
    openModal: (modal) => modalActionCreators.setModalComponentType(modal)
  };
};

// `state: any` matches `ITitleFunction` from @opengov/compose-app-router.
const buildTitle = (state: any) =>
  generatePageTitle('SetupOnlinePublication', selectEntityDisplayName(state));

const buildOnEnter = (
  expectsMenuId: boolean
) =>
  async (state: any, nextLocation: any, onSuccess: () => void, onError: any) => {
    const {
      entitySubdomain,
      publicationId: _publicationId
    }: { entitySubdomain: string; publicationId: string; menuId?: string } = nextLocation.params;
    await entityEffects
      .fetchRequestedOrCurrentEntity({
        requestedEntitySubdomain: entitySubdomain,
        currentEntity: selectCurrentEntity(state)
      })
      // `fetchRequestedOrCurrentEntity` is typed as `(value: ICurrentEntity) => any`
      // upstream but the runtime value is the full `IEntity`. Cast at the boundary.
      .then(async (rawEntity) => {
        const entity = rawEntity as unknown as IEntity;
        if (entity.subdomain !== entitySubdomain) {
          return onError(
            'Entity not found...',
            routerActions.replace.create({ pathname: publicationListPath(entity.subdomain) })
          );
        }
        await coreFeatureFlagEffects.getFlags().then(async (flags) => {
          if (flags && flags['product-stories-maintenance-window']) {
            return onError(
              'Redirecting to maintenance page...',
              routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
            );
          }
          onSuccess();
        });
      });
  };

export const setupOnlinePublicationRoute = {
  name: 'SetupOnlinePublication',
  title: buildTitle,
  selector: buildSelector,
  component: SetupOnlinePublication,
  onEnter: ensureAll([onEnterStoriesRoutes, buildOnEnter(false)])
};

export const onlinePublicationMenuRoute = {
  name: 'SetupOnlinePublication',
  title: buildTitle,
  selector: buildSelector,
  component: SetupOnlinePublication,
  onEnter: ensureAll([onEnterStoriesRoutes, buildOnEnter(true)])
};
